import type { FilterState, SaleRecord } from './types';

interface FilterRecordOptions {
  sqftBounds: { min: number; max: number } | null;
  isFullSqftRange: () => boolean;
  saleTypeOptions: string[];
  landClassOptions: string[];
}

/**
 * Applies the active client-side facet filters to the loaded sales records.
 * Records with missing values stay visible unless an enabled filter needs that field.
 */
export function filterRecords(records: SaleRecord[], filters: FilterState, options: FilterRecordOptions): SaleRecord[] {
  // `saleDateMs` comes from serialized UTC sale dates, so expand date-only filter
  // values as UTC days to keep boundary sales from shifting by the user's timezone.
  const fromMs = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00.000Z`).getTime() : null;
  const toMs = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999Z`).getTime() : null;

  return records.filter((record) => {
    if (record.saleDateMs !== null && fromMs !== null && record.saleDateMs < fromMs) return false;
    if (record.saleDateMs !== null && toMs !== null && record.saleDateMs > toMs) return false;
    if (filters.minPrice !== null && (record.price === null || record.price < filters.minPrice)) return false;
    if (filters.maxPrice !== null && (record.price === null || record.price > filters.maxPrice)) return false;
    // Sqft is a derived facet, so only enforce it once the user has narrowed away from the full available range.
    if (options.sqftBounds && filters.sqftMin !== null && filters.sqftMax !== null && !options.isFullSqftRange()) {
      if (record.buildingSize === null || record.buildingSize < filters.sqftMin || record.buildingSize > filters.sqftMax) {
        return false;
      }
    }
    // These multi-select facets behave as "all values allowed" until the user deselects at least one option.
    if (options.saleTypeOptions.length > 0 && filters.saleTypes.length < options.saleTypeOptions.length) {
      if (!filters.saleTypes.includes(record.saleType)) return false;
    }
    if (options.landClassOptions.length > 0 && filters.landClasses.length < options.landClassOptions.length) {
      if (!filters.landClasses.includes(record.landClass)) return false;
    }
    if (filters.acreageMin !== null && (record.acreage === null || record.acreage < filters.acreageMin)) return false;
    if (filters.acreageMax !== null && (record.acreage === null || record.acreage > filters.acreageMax)) return false;
    return true;
  });
}
